import { useCallback, useRef, useState } from "react";

/**
 * Keeps the pending intent observable synchronously as well as renderable.
 *
 * Explain activation can race connection readiness, subscription invalidation,
 * and a second click before React commits state. The ref is the authoritative
 * single-flight token; state only publishes that token to rendering/effects.
 */
export const useSynchronizedPending = <T>() => {
  const pendingRef = useRef<T | null>(null);
  const [pending, setPendingState] = useState<T | null>(null);

  const setPending = useCallback((next: T | null) => {
    pendingRef.current = next;
    setPendingState(next);
  }, []);

  const clearPending = useCallback((expected?: T): boolean => {
    if (expected !== undefined && pendingRef.current !== expected) return false;
    pendingRef.current = null;
    setPendingState((current) =>
      expected === undefined || current === expected ? null : current,
    );
    return true;
  }, []);

  const replacePending = useCallback((expected: T, next: T): boolean => {
    if (pendingRef.current !== expected) return false;
    pendingRef.current = next;
    setPendingState((current) => (current === expected ? next : current));
    return true;
  }, []);

  return Object.freeze({
    pending,
    pendingRef,
    setPending,
    clearPending,
    replacePending,
  });
};
